import { useEffect, useMemo, useState } from 'react'
import { Search, X, Loader2, Scissors, RotateCw, Lock } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { useEphemeralState } from '../../lib/persist.js'
import { relabelBranch } from '../../lib/ticket.js'
import StockCutDrawer from '../../components/stock/StockCutDrawer.jsx'
import StockRecordDrawer from '../../components/stock/StockRecordDrawer.jsx'
import {
  rows, todayISO, daysAgoISO, typeTone, fmtTime, rowLevel, stockRowEditState,
  stockDefaultBranch, STOCK_TYPES, TYPE_CUT, TYPE_ADJUST,
} from '../../lib/stock.js'
import Pager from '../../components/Pager.jsx'

// ตัด Stock — the history IS the page.
//
// Recording a cut is a 30-second errand; living with the record is the rest of
// the job. So the default view answers "what did we cut?" and "is that right?",
// and cutting is an action launched from it (drawer) rather than the landing
// state. Coming back from a save lands you on the proof it worked.
//
// Type defaults to เบิกใช้ — this page is ประวัติตัด Stock — but the filter
// stays, because a level that looks wrong is as likely to be a transfer or a
// ปรับ as a cut, and hiding those would make the page lie by omission.

const PER_PAGE = 50

function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = String(iso).split('-')
  if (!y) return iso
  return `${Number(d)}/${Number(m)}/${String(y).slice(2)}`
}

export default function StockCut() {
  const me = useMe()

  // Filters are remembered across in-app navigation (leave the page, come back →
  // still your last selection) but reset to defaults on a full browser refresh —
  // hence useEphemeralState (module memory), not usePersistedState (localStorage).
  const [branchId, setBranchId] = useEphemeralState('stock:rec:branch', null)
  const [type, setType] = useEphemeralState('stock:rec:type', TYPE_CUT)
  const [start, setStart] = useEphemeralState('stock:rec:start', todayISO)
  const [end, setEnd] = useEphemeralState('stock:rec:end', todayISO)
  const [q, setQ] = useEphemeralState('stock:rec:q', '')
  const [page, setPage] = useState(1)

  const [branches, setBranches] = useState([])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  const [cutOpen, setCutOpen] = useState(false)
  const [active, setActive] = useState(null)   // row open in the record drawer
  const [toast, setToast] = useState(null)

  useEffect(() => {
    if (branchId == null && me?.branch_id) setBranchId(me.branch_id)
  }, [me, branchId, setBranchId])

  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranches(rows(r))).catch(() => {})
  }, [])

  // A narrower filter must not strand you on page 5 of a 2-page result.
  useEffect(() => { setPage(1) }, [branchId, type, start, end])

  useEffect(() => {
    let alive = true
    setLoading(true)
    apiGet('/api/stock', {
      branch_id: branchId || undefined,
      type: type || undefined,
      start_date: start || undefined,
      end_date: end || undefined,
      page,
      per_page: PER_PAGE,
    })
      .then((r) => { if (alive) { setData(r); setError(null) } })
      .catch((e) => { if (alive) setError(e.message || 'โหลดประวัติไม่สำเร็จ') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [branchId, type, start, end, page, reloadKey])

  const items = useMemo(() => rows(data), [data])
  const refresh = () => setReloadKey((k) => k + 1)

  // Client-side, current page only — GET /stock takes inventory_id, not a name,
  // so there is no server-side item search to hand this off to.
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return items
    return items.filter((r) =>
      String(r?.inventory?.Name || '').toLowerCase().includes(needle) ||
      String(r?.inventory?.subCategory || '').toLowerCase().includes(needle) ||
      String(r?.remark || '').toLowerCase().includes(needle) ||
      String(r?.Creator || '').toLowerCase().includes(needle))
  }, [items, q])

  const total = Number(data?.itemsTotal) || 0
  const pageTotal = Number(data?.pageTotal) || 1

  return (
    <div className="px-10 py-8 max-w-[1150px]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight mb-1">ตัด Stock</h1>
          <p className="text-[14px] text-slate-500">ประวัติการตัดของ — ตรวจสอบและแก้ไขรายการที่บันทึกไว้</p>
        </div>
        <button onClick={() => setCutOpen(true)}
          className="shrink-0 rounded-xl bg-brand-600 px-5 py-2.5 text-[14.5px] font-semibold text-white hover:bg-brand-700 flex items-center gap-2">
          <Scissors size={16} />ตัด Stock
        </button>
      </div>

      {/* Server-side filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-[13px] text-slate-600">
          <span className="text-slate-400">สาขา</span>
          <select value={branchId ?? ''} onChange={(e) => setBranchId(Number(e.target.value) || null)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800">
            <option value="">ทุกสาขา</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{relabelBranch(b.name || b.Name) || `สาขา ${b.id}`}</option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-[13px] text-slate-600">
          <span className="text-slate-400">ประเภท</span>
          <select value={type} onChange={(e) => setType(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800">
            <option value="">ทุกประเภท</option>
            {STOCK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>

        <label className="flex items-center gap-2 text-[13px] text-slate-600">
          <span className="text-slate-400">จาก</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800" />
        </label>
        <label className="flex items-center gap-2 text-[13px] text-slate-600">
          <span className="text-slate-400">ถึง</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800" />
        </label>

        <div className="flex gap-1.5">
          <QuickRange label="วันนี้" onClick={() => { setStart(todayISO()); setEnd(todayISO()) }} />
          <QuickRange label="7 วัน" onClick={() => { setStart(daysAgoISO(7)); setEnd(todayISO()) }} />
          <QuickRange label="30 วัน" onClick={() => { setStart(daysAgoISO(30)); setEnd(todayISO()) }} />
        </div>

        <button onClick={refresh} title="โหลดใหม่"
          className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:text-brand-600 hover:border-brand-300">
          <RotateCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="relative mb-4 max-w-[420px]">
        <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="กรองในหน้านี้… (ชื่อของ · หมายเหตุ · ผู้บันทึก)"
          className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-10 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 focus:border-brand-300 focus:ring-2 focus:ring-brand-100 outline-none" />
        {q && (
          <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X size={16} />
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{error}</div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] font-medium uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-5 py-3 font-medium">วันที่</th>
                <th className="px-5 py-3 font-medium">ของ</th>
                <th className="px-5 py-3 font-medium">สาขา</th>
                <th className="px-5 py-3 font-medium">ประเภท</th>
                <th className="px-5 py-3 font-medium text-right">จำนวน</th>
                <th className="px-5 py-3 font-medium">หน่วย</th>
                {/* stock_N is today's level for that item+branch, NOT the balance
                    after this row — "ปัจจุบัน" is load-bearing. See lib/stock.js. */}
                <th className="px-5 py-3 font-medium text-right whitespace-nowrap">
                  คงเหลือ<span className="normal-case text-slate-300"> (ปัจจุบัน)</span>
                </th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={8} className="px-5 py-16 text-center text-slate-400">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…
                </td></tr>
              )}
              {!loading && !visible.length && (
                <tr><td colSpan={8} className="px-5 py-16 text-center text-slate-400">
                  {q ? `ไม่พบรายการที่ตรงกับ “${q}” ในหน้านี้` : 'ไม่พบรายการในช่วงที่เลือก'}
                </td></tr>
              )}
              {!loading && visible.map((r) => {
                const n = Number(r?.N) || 0
                const level = rowLevel(r)
                const { editable } = stockRowEditState(me, r)
                return (
                  <tr key={r.id} onClick={() => setActive(r)}
                    className="hover:bg-slate-50/60 cursor-pointer">
                    <td className="px-5 py-3 align-top whitespace-nowrap text-slate-500 tabular-nums">
                      <div>{fmtDate(r.date)}</div>
                      <div className="text-[11.5px] text-slate-400">{fmtTime(r.Creation_Date)}</div>
                    </td>
                    <td className="px-5 py-3 align-top">
                      <div className="text-slate-800">{r?.inventory?.Name || `#${r.inventory_id}`}</div>
                      {r?.inventory?.subCategory && (
                        <div className="text-[11.5px] text-slate-400">{r.inventory.subCategory}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 align-top whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5 text-slate-600">
                        {r?.branch?.color && (
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: r.branch.color }} />
                        )}
                        {relabelBranch(r?.branch?.name) || `#${r.branch_id}`}
                      </span>
                    </td>
                    <td className="px-5 py-3 align-top whitespace-nowrap">
                      <span className={`inline-block rounded-full border px-2 py-0.5 text-[11.5px] font-medium ${typeTone(r.type)}`}>
                        {r.type || '—'}
                      </span>
                    </td>
                    <td className={`px-5 py-3 align-top text-right tabular-nums font-semibold whitespace-nowrap ${
                      n < 0 ? 'text-rose-600' : 'text-emerald-600'
                    }`}>
                      {n > 0 ? `+${n}` : n}
                    </td>
                    <td className="px-5 py-3 align-top text-slate-400 whitespace-nowrap">
                      {r?.inventory?.unit || '—'}
                    </td>
                    <td className="px-5 py-3 align-top text-right tabular-nums text-slate-500"
                        title="คงเหลือปัจจุบันของของชิ้นนี้ที่สาขานี้ (ไม่ใช่ยอดหลังรายการนี้)">
                      {level == null ? '—' : level}
                    </td>
                    <td className="px-3 py-3 align-top text-slate-300">
                      {!editable && <Lock size={13} title="แก้ไขไม่ได้" />}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-slate-100 px-5 py-3 text-[13px] text-slate-500">
          <div>
            {q
              ? <>แสดง <b className="text-slate-700">{visible.length}</b> จาก {items.length} รายการในหน้านี้ · ทั้งหมด {total}</>
              : <>ทั้งหมด <b className="text-slate-700">{total}</b> รายการ</>}
          </div>
          <Pager page={page} pageTotal={pageTotal} onChange={setPage} disabled={loading} />
        </div>
      </div>

      {cutOpen && (
        <StockCutDrawer
          branches={branches}
          defaultBranchId={stockDefaultBranch(branchId, me?.branch_id)}
          onClose={() => setCutOpen(false)}
          onSaved={(n, savedType) => {
            setCutOpen(false)
            const word = savedType === TYPE_ADJUST ? 'ปรับ Stock' : 'ตัด Stock'
            setToast(`${word} ${n} รายการแล้ว`)
            // Land on the proof: today's rows of the type just written.
            setStart(todayISO()); setEnd(todayISO()); setType(savedType || TYPE_CUT); setPage(1)
            refresh()
          }}
        />
      )}

      {active && (
        <StockRecordDrawer
          row={active}
          onClose={() => setActive(null)}
          onChanged={refresh}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-xl bg-slate-900 text-white px-5 py-3 text-[14px] flex items-center gap-3 shadow-lg z-40">
          {toast}
          <button onClick={() => setToast(null)} className="text-slate-400 hover:text-white"><X size={15} /></button>
        </div>
      )}
    </div>
  )
}

function QuickRange({ label, onClick }) {
  return (
    <button onClick={onClick}
      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12.5px] text-slate-600 hover:border-brand-300 hover:text-brand-700">
      {label}
    </button>
  )
}
